import { useRouteError, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '@/components/ui/Button';
import { Sentry } from '@/lib/sentry';

export function ErrorBoundaryView() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream via-cream-warm to-cream-light relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[20%] left-[20%] w-56 h-56 lg:w-80 lg:h-80 rounded-full bg-error/[0.05] animate-[float_8s_ease-in-out_infinite]" />
        <div className="absolute bottom-[15%] right-[18%] w-64 h-64 lg:w-96 lg:h-96 rounded-full bg-primary/[0.04] animate-[float_10s_ease-in-out_infinite_1s]" />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto w-full px-6 relative z-10 text-center">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-sans text-xs uppercase tracking-[0.24em] text-text-muted mb-4"
        >
          Unexpected error
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.05 }}
          className="font-display italic font-light text-5xl md:text-7xl leading-[1.05] text-text-primary mb-5"
        >
          Something broke.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="font-sans text-base md:text-lg text-text-secondary mb-10"
        >
          It's not you, it's us. Try reloading, or head back home and we'll
          start fresh.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="w-full max-w-xs space-y-3"
        >
          <Button fullWidth onClick={() => window.location.reload()}>
            Try again
          </Button>
          <Button
            fullWidth
            variant="secondary"
            onClick={() => navigate('/', { replace: true })}
          >
            Take me home
          </Button>
        </motion.div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
        }
      `}</style>
    </div>
  );
}

export default function ErrorBoundaryPage() {
  const error = useRouteError();

  useEffect(() => {
    Sentry.withScope((s) => {
      s.setTag('error.boundary', 'route');
      s.setTag('route.pathname', window.location.pathname);
      Sentry.captureException(error);
    });
  }, [error]);

  return <ErrorBoundaryView />;
}
